import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  listAiProviders,
  listUserAiProviderTestModels,
  testUserAiProviderKeyWithModel,
} from '../utils/api';

function getDraftStorageKey(providerId) {
  return `apiKeyTestDraft:${providerId}`;
}

export default function SettingsApiKeyTestPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const providerId = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return (params.get('providerId') || '').trim();
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [provider, setProvider] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!providerId) {
        setError('Missing provider. Return to User Settings and retry.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const [providerRes, modelsRes] = await Promise.all([
          listAiProviders(),
          listUserAiProviderTestModels({ providerId }),
        ]);
        if (cancelled) return;

        const activeProvider = (providerRes.providers || []).find((p) => String(p.id || '') === providerId);
        setProvider(activeProvider || modelsRes.provider || null);

        const llmModels = Array.isArray(modelsRes.models) ? modelsRes.models : [];
        setModels(llmModels);
        setSelectedModel(llmModels[0]?.id || '');
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || 'Failed to load provider test models');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [providerId]);

  const handleBack = () => {
    navigate('/settings/user');
  };

  const handleRunTest = async () => {
    if (!providerId || !selectedModel) return;
    setTesting(true);
    setResult(null);
    setError('');

    let draftKey;
    try {
      draftKey = window.sessionStorage.getItem(getDraftStorageKey(providerId)) || undefined;
    } catch (_) {
      draftKey = undefined;
    }

    try {
      const response = await testUserAiProviderKeyWithModel({
        providerId,
        modelId: selectedModel,
        apiKey: draftKey,
      });
      setResult(response);
    } catch (testError) {
      setError(testError.message || 'Model test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-category">
        <h3>
          <i className="fas fa-vial" /> API Key Model Test
        </h3>

        <div className="setting-item">
          <div>
            <div className="setting-label">Provider</div>
            <div className="setting-description">
              {provider?.name || provider?.slug || providerId || 'Unknown provider'}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading"><i className="fas fa-spinner fa-spin" /> Loading models...</div>
        ) : (
          <>
            {error && (
              <div className="alert alert-error" style={{ marginBottom: '12px' }}>
                <i className="fas fa-exclamation-circle" /> {error}
              </div>
            )}

            <div className="setting-item">
              <div style={{ width: '100%' }}>
                <div className="setting-label">Model (LLM only)</div>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  disabled={testing || models.length === 0}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name || model.id}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="settings-save-section">
              <button className="btn btn-secondary" onClick={handleBack} disabled={testing}>
                Back to User Settings
              </button>
              <button
                className="btn btn-primary"
                onClick={handleRunTest}
                disabled={testing || !selectedModel}
              >
                {testing ? (<><i className="fas fa-spinner fa-spin" /> Testing...</>) : (<><i className="fas fa-play" /> Run 1-token test</>)}
              </button>
            </div>

            {result && (
              <div className={`alert alert-${result.valid ? 'success' : 'error'}`} style={{ marginTop: '12px' }}>
                <i className={`fas fa-${result.valid ? 'check-circle' : 'exclamation-circle'}`} />
                {result.valid ? ' API key validated successfully.' : ` Validation failed: ${result.error || result.message || result.status}`}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
